/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  TLConnectionStatusResponse,
  TLConnectUrlResponse,
  TLDisconnectResponse,
  TLHTTPValidationError,
  WorkiqCallbackParams,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Workiq Callback
 */
export const workiqCallback = (
  params?: WorkiqCallbackParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>({ url: `/mcp/workiq/callback`, method: 'GET', params, signal }, options);
};

export const getWorkiqCallbackQueryKey = (params?: WorkiqCallbackParams) => {
  return ['mcp', 'workiq', 'callback', ...(params ? [params] : [])] as const;
};

export const getWorkiqCallbackQueryOptions = <
  TData = Awaited<ReturnType<typeof workiqCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: WorkiqCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof workiqCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getWorkiqCallbackQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof workiqCallback>>> = ({ signal }) =>
    workiqCallback(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof workiqCallback>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type WorkiqCallbackQueryResult = NonNullable<Awaited<ReturnType<typeof workiqCallback>>>;
export type WorkiqCallbackQueryError = ErrorType<TLHTTPValidationError>;

export function useWorkiqCallback<
  TData = Awaited<ReturnType<typeof workiqCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | WorkiqCallbackParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof workiqCallback>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof workiqCallback>>,
          TError,
          Awaited<ReturnType<typeof workiqCallback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useWorkiqCallback<
  TData = Awaited<ReturnType<typeof workiqCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: WorkiqCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof workiqCallback>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof workiqCallback>>,
          TError,
          Awaited<ReturnType<typeof workiqCallback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useWorkiqCallback<
  TData = Awaited<ReturnType<typeof workiqCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: WorkiqCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof workiqCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Workiq Callback
 */

export function useWorkiqCallback<
  TData = Awaited<ReturnType<typeof workiqCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: WorkiqCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof workiqCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getWorkiqCallbackQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Workiq Callback
 */
export const prefetchWorkiqCallbackQuery = async <
  TData = Awaited<ReturnType<typeof workiqCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: WorkiqCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof workiqCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getWorkiqCallbackQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Workiq Callback
 */
export const invalidateWorkiqCallback = async (
  queryClient: QueryClient,
  params?: WorkiqCallbackParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getWorkiqCallbackQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Connect Url
 */
export const connectUrl = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLConnectUrlResponse>({ url: `/mcp/workiq/connect-url`, method: 'POST', signal }, options);
};

export const getConnectUrlQueryKey = () => {
  return ['POST', 'mcp', 'workiq', 'connect-url'] as const;
};

export const getConnectUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof connectUrl>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof connectUrl>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getConnectUrlQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof connectUrl>>> = ({ signal }) =>
    connectUrl(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof connectUrl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ConnectUrlQueryResult = NonNullable<Awaited<ReturnType<typeof connectUrl>>>;
export type ConnectUrlQueryError = ErrorType<unknown>;

export function useConnectUrl<TData = Awaited<ReturnType<typeof connectUrl>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof connectUrl>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof connectUrl>>,
          TError,
          Awaited<ReturnType<typeof connectUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useConnectUrl<TData = Awaited<ReturnType<typeof connectUrl>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof connectUrl>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof connectUrl>>,
          TError,
          Awaited<ReturnType<typeof connectUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useConnectUrl<TData = Awaited<ReturnType<typeof connectUrl>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof connectUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Connect Url
 */

export function useConnectUrl<TData = Awaited<ReturnType<typeof connectUrl>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof connectUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getConnectUrlQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Connect Url
 */
export const prefetchConnectUrlQuery = async <
  TData = Awaited<ReturnType<typeof connectUrl>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof connectUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getConnectUrlQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Connect Url
 */
export const invalidateConnectUrl = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getConnectUrlQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Disconnect
 */
export const disconnect = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLDisconnectResponse>({ url: `/mcp/workiq/disconnect`, method: 'DELETE', signal }, options);
};

export const getDisconnectQueryKey = () => {
  return ['DELETE', 'mcp', 'workiq', 'disconnect'] as const;
};

export const getDisconnectQueryOptions = <
  TData = Awaited<ReturnType<typeof disconnect>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disconnect>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDisconnectQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof disconnect>>> = ({ signal }) =>
    disconnect(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof disconnect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DisconnectQueryResult = NonNullable<Awaited<ReturnType<typeof disconnect>>>;
export type DisconnectQueryError = ErrorType<unknown>;

export function useDisconnect<TData = Awaited<ReturnType<typeof disconnect>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof disconnect>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof disconnect>>,
          TError,
          Awaited<ReturnType<typeof disconnect>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDisconnect<TData = Awaited<ReturnType<typeof disconnect>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disconnect>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof disconnect>>,
          TError,
          Awaited<ReturnType<typeof disconnect>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDisconnect<TData = Awaited<ReturnType<typeof disconnect>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disconnect>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Disconnect
 */

export function useDisconnect<TData = Awaited<ReturnType<typeof disconnect>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disconnect>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDisconnectQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Disconnect
 */
export const prefetchDisconnectQuery = async <
  TData = Awaited<ReturnType<typeof disconnect>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disconnect>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDisconnectQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Disconnect
 */
export const invalidateDisconnect = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDisconnectQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Status
 */
export const status = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLConnectionStatusResponse>({ url: `/mcp/workiq/status`, method: 'GET', signal }, options);
};

export const getStatusQueryKey = () => {
  return ['mcp', 'workiq', 'status'] as const;
};

export const getStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof status>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof status>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof status>>> = ({ signal }) => status(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof status>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StatusQueryResult = NonNullable<Awaited<ReturnType<typeof status>>>;
export type StatusQueryError = ErrorType<unknown>;

export function useStatus<TData = Awaited<ReturnType<typeof status>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof status>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof status>>, TError, Awaited<ReturnType<typeof status>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStatus<TData = Awaited<ReturnType<typeof status>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof status>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof status>>, TError, Awaited<ReturnType<typeof status>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStatus<TData = Awaited<ReturnType<typeof status>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof status>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Status
 */

export function useStatus<TData = Awaited<ReturnType<typeof status>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof status>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Status
 */
export const prefetchStatusQuery = async <TData = Awaited<ReturnType<typeof status>>, TError = ErrorType<unknown>>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof status>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStatusQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Status
 */
export const invalidateStatus = async (queryClient: QueryClient, options?: InvalidateOptions): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getStatusQueryKey() }, options);

  return queryClient;
};
